import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { SupplierSheet } from "@/components/SupplierSheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatKr, formatDate, formatOrg } from "@/lib/format";
import { toast } from "sonner";
import { Building2, Pencil, Search, Tags, ExternalLink, Plus, User, MapPin } from "lucide-react";

export default function SuppliersPage() {
  const { canWrite } = useAuth();
  const [items, setItems] = useState(null);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [sheet, setSheet] = useState({ open: false, supplier: null });

  const load = () => api.get("/suppliers").then((r) => setItems(r.data.items)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); api.get("/categories").then((r) => setCategories(r.data.items)).catch(() => {}); }, []);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (items || []).filter((x) => !s || [x.name, x.org_number, x.city, x.email].some((v) => (v || "").toLowerCase().includes(s)));
  }, [items, q]);

  const setCategory = async (s, categoryId) => {
    try {
      await api.put(`/suppliers/${s.id}`, { default_category_id: categoryId === "ingen" ? null : categoryId });
      toast.success("Standardkategori oppdatert");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const applyCategory = async (s) => {
    try {
      const r = await api.post(`/suppliers/${s.id}/apply-category`);
      toast.success(`Kategori satt på ${r.data.updated} fakturaer`);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const openNew = () => setSheet({ open: true, supplier: null });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leverandører"
        description="Alle som sender deg regninger. Legg inn firma via Brønnøysundregistrene, sett standardkategori og få varsel om kontonummeret endrer seg."
        actions={canWrite ? <Button onClick={openNew} data-testid="supplier-add-button"><Plus className="h-4 w-4 mr-2" /> Legg til leverandør</Button> : null}
      />
      <div className="relative max-w-sm">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk leverandør, org.nr eller sted…" className="pl-9" data-testid="supplier-search-input" />
      </div>

      {items === null ? <div className="grid gap-3 md:grid-cols-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
        : filtered.length === 0 ? <EmptyState icon={Building2} title={q ? "Ingen treff" : "Ingen leverandører ennå"} description={q ? "Prøv et annet søk." : "Leverandører opprettes automatisk når du laster opp eller registrerer regninger – eller legg dem inn nå med org.nr fra Brønnøysundregistrene."} actionLabel={q || !canWrite ? undefined : "Legg til leverandør"} onAction={canWrite ? openNew : undefined} testId="suppliers-empty-state" />
        : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="suppliers-grid">
            {filtered.map((s) => {
              const Icon = s.type === "person" ? User : Building2;
              const place = [s.postal_code, s.city].filter(Boolean).join(" ");
              return (
                <Card key={s.id} className="card-lift" data-testid={`supplier-card-${s.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <span className="h-10 w-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></span>
                        <div className="min-w-0">
                          <h3 className="font-semibold truncate" data-testid={`supplier-name-${s.id}`}>{s.name}</h3>
                          <p className="text-xs text-muted-foreground">{s.type === "person" ? "Privatperson" : s.org_number ? `Org.nr ${formatOrg(s.org_number)}` : "Org.nr ukjent"}{s.last_due_date ? ` · siste forfall ${formatDate(s.last_due_date)}` : ""}</p>
                          {s.address || place ? <p className="text-xs text-muted-foreground flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" /> {[s.address, place].filter(Boolean).join(", ")}</p> : null}
                        </div>
                      </div>
                      {canWrite ? <Button variant="ghost" size="icon" onClick={() => setSheet({ open: true, supplier: s })} aria-label="Rediger" data-testid={`supplier-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button> : null}
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
                      <div><p className="text-xs text-muted-foreground">Fakturaer</p><p className="font-medium tnum">{s.invoice_count}</p></div>
                      <div><p className="text-xs text-muted-foreground">Ubetalt</p><p className="font-medium tnum">{s.unpaid_count}</p></div>
                      <div><p className="text-xs text-muted-foreground">Totalt</p><p className="font-medium tnum whitespace-nowrap">{formatKr(s.total_amount)}</p></div>
                    </div>
                    <div className="mt-3 min-h-[40px]">
                      <p className="text-xs text-muted-foreground">Kontonummer{s.account_numbers_formatted?.length > 1 ? <span className="text-flag font-medium"> · {s.account_numbers_formatted.length} ulike registrert</span> : ""}</p>
                      <p className="text-sm tnum truncate">{s.account_numbers_formatted?.length ? s.account_numbers_formatted.join(", ") : <span className="text-muted-foreground">–</span>}</p>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <Select value={s.default_category_id || "ingen"} onValueChange={(v) => setCategory(s, v)} disabled={!canWrite}>
                        <SelectTrigger className="h-9 flex-1" aria-label="Standardkategori" data-testid={`supplier-category-select-${s.id}`}><SelectValue placeholder="Standardkategori" /></SelectTrigger>
                        <SelectContent className="bg-card"><SelectItem value="ingen">Ingen standardkategori</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                      {s.default_category_id && canWrite ? (
                        <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={() => applyCategory(s)} title="Bruk kategorien på alle fakturaer fra leverandøren" data-testid={`supplier-apply-category-${s.id}`}><Tags className="h-4 w-4 mr-1" /> Bruk på alle</Button>
                      ) : null}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">{s.default_category_id && catMap[s.default_category_id] ? `Nye fakturaer får «${catMap[s.default_category_id].name}»` : "Nye fakturaer får ingen kategori automatisk"}</p>
                      <Button variant="link" size="sm" className="h-auto p-0" asChild><Link to={`/fakturaer?supplier_id=${s.id}`} data-testid={`supplier-invoices-link-${s.id}`}>Se fakturaer <ExternalLink className="h-3.5 w-3.5 ml-1" /></Link></Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      <SupplierSheet open={sheet.open} onOpenChange={(o) => setSheet((s) => ({ ...s, open: o }))} supplier={sheet.supplier} categories={categories} onSaved={() => load()} />
    </div>
  );
}
